import { useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { sectionStyles, labelStyles, inputStyles } from './styles';
import { resetUserPassword, setUserPassword } from '~/services/users';
import toast from 'react-hot-toast';

interface Props {
    userId: number;
    userName: string;
    onPasswordChanged?: () => void;
}

const PasswordManagement: React.FC<Props> = ({ userId, userName, onPasswordChanged }) => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSetting, setIsSetting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [showResendModal, setShowResendModal] = useState(false);

    const handleSetPassword = async () => {
        if (password.length < 8) {
            toast.error('Password must be at least 8 characters');
            return;
        }
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setIsSetting(true);
        try {
            await setUserPassword(userId, password);
            toast.success(`Password set for ${userName}`);
            setPassword('');
            setConfirmPassword('');
            onPasswordChanged?.();
        } catch (error: any) {
            toast.error(error?.message || 'Failed to set password');
        } finally {
            setIsSetting(false);
        }
    };

    const handleResendReset = async () => {
        setIsResending(true);
        try {
            await resetUserPassword(userId);
            toast.success(`Password reset email sent to ${userName}`);
            setShowResendModal(false);
            onPasswordChanged?.();
        } catch (error) {
            toast.error('Failed to send password reset email');
        } finally {
            setIsResending(false);
        }
    };

    return (
        <>
            <h2 className={sectionStyles}>PASSWORD MANAGEMENT</h2>

            <label className={labelStyles}>Set Password</label>
            <input
                type='password'
                className={`${inputStyles} mt-2 w-full`}
                placeholder='New password'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
            />

            <label className={labelStyles}>Confirm Password</label>
            <input
                type='password'
                className={`${inputStyles} mt-2 w-full`}
                placeholder='Confirm password'
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <button
                type='button'
                onClick={handleSetPassword}
                disabled={isSetting || !password || !confirmPassword}
                className='mt-4 w-full rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
            >
                {isSetting ? 'Setting...' : 'Set Password'}
            </button>

            <div className='mt-4 border-t border-gray-200 pt-4'>
                <button
                    type='button'
                    onClick={() => setShowResendModal(true)}
                    disabled={isResending}
                    className='w-full rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                >
                    Send Password Reset Email
                </button>
            </div>

            {showResendModal && (
                <div
                    className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75'
                    onClick={!isResending ? () => setShowResendModal(false) : undefined}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
                    >
                        <button
                            className='text-gray-600 hover:text-gray-900 absolute right-4 top-4 disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={() => setShowResendModal(false)}
                            disabled={isResending}
                        >
                            <RxCross2 />
                        </button>
                        <div className='p-8'>
                            <h2 className='mb-[30px] text-[22px] font-bold'>
                                Send Password Reset Email
                            </h2>
                            <p className='mb-[30px]'>
                                Are you sure you want to send a password reset email to{' '}
                                <strong>{userName}</strong>?
                            </p>
                            <div className='flex justify-start space-x-4'>
                                <button
                                    onClick={() => setShowResendModal(false)}
                                    disabled={isResending}
                                    className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleResendReset}
                                    disabled={isResending}
                                    className='rounded-full bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
                                >
                                    {isResending ? 'Sending...' : 'Send'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PasswordManagement;
